import benchmarks from 'api/benchmarks';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import { showSuccess } from 'helpers/showSuccess';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Benchmark, BenchmarkTypes } from 'types/Benchmark';
import {
  BenchmarkForm,
  CalculationFrequency,
  RebalanceFrequency,
} from '../BenchmarkForm/BenchmarkForm';
import { EditModalStyle } from './EditModalStyles';
import { useHistory } from 'react-router-dom';
import { paths } from 'routes';
import { showWarning } from 'helpers/showWarning';
import { useYupValidationResolver } from 'helpers/form';
import { format } from 'date-fns';
import { ProblemDetails } from 'helpers/problemDetails';
import { useProblemsHandler } from 'modules/BenchmarkForm/hooks/useProblemsHandler';

type Properties = {
  benchmark: Benchmark;
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  validationScheme: unknown;
};

export const EditModal = (props: Properties) => {
  const { benchmark, visible, onClose, onConfirm, validationScheme } = props;
  const resolver = useYupValidationResolver(validationScheme);

  const methods = useForm<any /* setCustomLagDate type issue */>({
    defaultValues: {
      ...benchmark,
      customLagDate: undefined,
      setCustomLagDate: undefined,
    },
    resolver,
  });

  const history = useHistory();

  const [disableSaveButton, setDisableSaveButton] = useState(true);

  const { mutate, isSuccess, data, isLoading }: any =
    benchmarks.SaveBenchmarks();

  const [redirect, setRedirect] = useState(false);
  const [problems, setProblems] = useState<ProblemDetails>();

  useProblemsHandler(methods.setError, problems);

  useEffect(() => {
    if (isSuccess) {
      if (redirect) {
        showWarning(
          'Your changes and lag date have been saved, but you will need to run the benchmark to see the latest data.'
        );
        history.push(paths.manageJobs, { recurring: false });
      } else {
        showSuccess('Your changes have been saved.');
        onConfirm();
      }
      methods.reset(methods.getValues());
    }
  }, [isSuccess, isLoading, data]);

  const onSubmit = (data: any) => {
    data.isLagged = data.isLagged === 'true';
    data.includeBookedValues = data.includeBookedValues === 'true';
    data.useDailyToMonthlyLinking = data.useDailyToMonthlyLinking === 'true';
    data.useLocalCurrencyReturns = data.useLocalCurrencyReturns === 'true';
    // When the push time windows radio group is hidden, a string 'false' is
    // present; when the control is shown, a boolean is present.  This
    // standardizes the field to send the required boolean to the back end.
    data.pushTimeWindowsToPortfolioAnalytics =
      data.pushTimeWindowsToPortfolioAnalytics === true ||
      data.pushTimeWindowsToPortfolioAnalytics === 'true';
    data.timeWindows = benchmark.timeWindows;

    if (data.typeId === BenchmarkTypes.AbsoluteReturn) {
      data.numberOfDaysInYearId =
        data.numberOfDaysInYearId !== undefined
          ? Number(data.numberOfDaysInYearId)
          : undefined;
    } else {
      delete data['numberOfDaysInYearId'];
    }

    if (data.calculationFrequencyId !== CalculationFrequency.day) {
      delete data['portfolioAnalyticsCalendarId'];
      delete data['numberOfDaysInYearId'];
    }

    if (!data.pushTimeWindowsToPortfolioAnalytics) {
      delete data['lookThroughDepth'];
    }

    if (
      data.customLagDate !== undefined &&
      typeof data.customLagDate !== 'string'
    ) {
      data.customLagDate = format(data.customLagDate, 'yyyy-MM-dd');
    }

    if (
      data.rebalanceFrequencyId === RebalanceFrequency.custom &&
      data.rebalanceFrequencyCustomMonths
    ) {
      const selectedMonths = data.rebalanceFrequencyCustomMonths.map(
        (x: { label: string; value: string }) => x.value
      );

      type availableMonths = {
        february: false;
        march: false;
        april: false;
        may: false;
        june: false;
        july: false;
        august: false;
        september: false;
        october: false;
        november: false;
        december: false;
        january: false;
      };

      const months: availableMonths = Object.assign(
        {},
        ...selectedMonths.map((k: string) => ({
          [k]: true,
        }))
      );

      data.rebalanceFrequencyCustomMonths = months;
    } else {
      data.rebalanceFrequencyCustomMonths = null;
    }

    mutate(
      { body: data },
      {
        onError: (data: any) => {
          setProblems(new ProblemDetails(data.response.data));
        },
      }
    );
  };

  useEffect(() => {
    setDisableSaveButton(
      Object.keys(methods.formState.dirtyFields).length === 0
    );
    if (methods.formState.dirtyFields !== undefined) {
      setRedirect(
        [
          methods.formState.dirtyFields.customLagDate,
          methods.formState.dirtyFields.setCustomLagDate,
          methods.formState.dirtyFields.isLagged,
        ].some((x) => x === true)
      );
    }
  }, [methods.formState]);

  const handleClose = () => {
    methods.reset();
    setDisableSaveButton(true);
    onClose();
  };

  return (
    <ConfirmModal
      size='xl'
      show={visible}
      onClose={handleClose}
      title={'Edit Benchmark'}
      confirmLabel={'Save'}
      onConfirm={() => methods.handleSubmit(onSubmit)()}
      disableSaveButton={disableSaveButton}
      isLoading={isLoading}
    >
      <EditModalStyle>
        <FormProvider {...methods}>
          <form>
            <BenchmarkForm benchmark={benchmark} />
          </form>
        </FormProvider>
      </EditModalStyle>
    </ConfirmModal>
  );
};
